import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";
import { GetInvoice, IGetInvoice } from "api/rpc/2024-04/facilityAdmin/order/invoice";

import { showError } from "redux/actions/ui";
import { IInvoice } from "redux/reducers/models/order";
import { useAppDispatch } from "hooks/redux";
import { capitalize } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";

import Page from "components/page/Page";
import { Badge } from "components/badge/Badge";
import DataTable from "../../../../../components/dataTable/DataTable";
import Search from "components/search/Search";
import { useTranslation } from "react-i18next";
import { THIS_YEAR } from "components/form/calendarField/helpers";
import YearDropFilter from "components/filters/YearDropFilter";
import "./invoice.scss";

const TABLE_LIMIT = 50;

export default function Invoices() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Array<IInvoice>>(undefined);

  const [filterState, setFilterState] = useState<IGetInvoice>({
    search: "",
    offset: 0,
    year: THIS_YEAR,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadInvoices(source.token);
    return () => source.cancel();
  }, [filterState]);

  async function loadInvoices(token?: CancelToken) {
    setInvoices(undefined);

    const res = await GetInvoice(
      {
        limit: TABLE_LIMIT,
        offset: filterState.offset,
        search: filterState.search,
        extended: true,
        year: filterState?.year,
      },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoices.001")));
      return;
    }

    setInvoices(res.data);
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const primaryAction = {
    content: t("secure.facility.order.invoices.invoices.002"),
    action: () => history.push("/admin/invoices/new"),
  };

  return (
    <Page title={t("secure.facility.order.invoices.invoices.003")} primaryAction={primaryAction}>
      <div className="flex flex-row flex-wrap gap-4 pb-4">
        <YearDropFilter
          handleApply={year => setFilterState({ ...filterState, year: year })}
          defaultCheckboxes={[filterState.year]}
          leftAligned
        />
      </div>
      <div className="pb-4">
        <Search
          searchCallback={searchValue => setFilterState(prev => ({ ...prev, search: searchValue, offset: 0 }))}
          historyKey="invoice-search"
          restrictPresetToRoutes={["admin/invoices"]}
        />
      </div>
      <DataTable
        columns={[
          { label: t("secure.facility.order.invoices.invoices.004") },
          { label: t("secure.facility.order.invoices.invoices.005") },
          { label: t("secure.facility.order.invoices.invoices.006") },
          { label: t("secure.facility.order.invoices.invoices.007") },
          { label: t("secure.facility.order.invoices.invoices.008") },
          { label: t("secure.facility.order.invoices.invoices.009") },
        ]}
        loading={invoices === undefined}
        footer={{
          tableLimit: TABLE_LIMIT,
          tableOffset: filterState.offset,
          handleTableOffset: direction => handleTableNavigation(direction),
          disableNextOffset: !(invoices?.length === TABLE_LIMIT) || invoices === undefined,
        }}
      >
        {invoices?.map(invoice => {
          const status =
            (invoice.voided_at || invoice.financial_status === "voided") ? "voided" : invoice.financial_status;

          return (
            <tr
              key={invoice.id}
              onClick={() =>
                status === "draft"
                  ? history.push("/admin/invoices/" + String(invoice.id) + "/edit")
                  : history.push("/admin/invoices/" + String(invoice.id))
              }
              className="clickable"
            >
              <td>{invoice.name}</td>
              <td>
                <span>{moment.utc(invoice.date).format("MMM DD, YYYY")}</span>
              </td>
              <td>{invoice.customer?.full_name}</td>
              <td>
                {invoice.tournament?.name && invoice.tournament.name}

                {invoice.hospitality_event?.name && invoice.hospitality_event.name}
              </td>
              <td>{statusBadge(status)}</td>
              <td>
                <LocaleCurrency amount={invoice.total_price} currency={invoice.currency} />
              </td>
            </tr>
          );
        })}
      </DataTable>
    </Page>
  );
}

const statusBadge = (status: string) => {
  switch (status) {
    case "draft":
      return (
        <Badge type="primary" outline className="invoice-status-badge--draft">
          Draft
        </Badge>
      );
    case "open":
      return <Badge type="warning">Open</Badge>;
    case "paid":
      return <Badge type="success">{capitalize(status.replace(/_/g, " "))}</Badge>;
    case "overpaid":
      return (
        <Badge type="warning" outline className="invoice-status-badge--overpaid">
          Overpaid
        </Badge>
      );
    case "partially_paid":
      return <Badge type="warning">{capitalize(status.replace(/_/g, " "))}</Badge>;
    case "voided":
      return <Badge type="error">Void</Badge>;
    default:
      return <Badge type="gray">{capitalize(status.replace(/_/g, " "))}</Badge>;
  }
};
